import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography } from '@mui/material';
import MySnackbar from './MySnackbar';
import { resetRejectionTracker } from '../api/emailProcess';

interface Props {
    allowedDomains: string[];
}

const BlockEmailButton: React.FC<Props> = ({ allowedDomains }) => {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{ msg: string; success: boolean } | null>(null);

    const validateDomain = (email: string): boolean => {
        const domain = email.split('@')[1]?.toLowerCase();
        if (!domain) return false;
        return allowedDomains.includes('*') || allowedDomains.includes(domain);
    };

    const handleBlock = async () => {
        if (!email) return;
        if (!validateDomain(email)) {
            setSnackbar({
                msg: `El dominio del email no pertenece a los permitidos por su empresa: ${allowedDomains.join(', ')}`,
                success: false,
            });
            return;
        }
        setLoading(true);
        try {
            const result = await resetRejectionTracker(email, true);
            if (result.success || result.blockEmail) {
                setSnackbar({ msg: `Email ${email} bloqueado exitosamente`, success: true });
                setOpen(false);
                setEmail('');
            } else {
                setSnackbar({ msg: result.message || 'Error al bloquear email', success: false });
            }
        } catch (error: any) {
            setSnackbar({ msg: error.message, success: false });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button variant="contained" color="error" onClick={() => setOpen(true)} sx={{ mr: 2 }}>
                Bloquear email
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Bloquear dirección de correo</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Solo se permite bloquear emails cuyos dominios estén registrados para su empresa:
                        <strong> {allowedDomains.join(', ')}</strong>
                    </Typography>
                    <TextField
                        autoFocus
                        size='small'
                        margin="dense"
                        label="Email a bloquear"
                        type="email"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancelar</Button>
                    <Button onClick={handleBlock} color="error" disabled={loading}>
                        {loading ? 'Bloqueando...' : 'Bloquear'}
                    </Button>
                </DialogActions>
            </Dialog>
            {snackbar && (
                <MySnackbar
                    open={true}
                    msg={snackbar.msg}
                    severity={snackbar.success ? 'success' : 'error'}
                    onClose={() => setSnackbar(null)}
                />
            )}
        </>
    );
};

export default BlockEmailButton;